import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, finalize, forkJoin, map } from 'rxjs';
import {
  BreadcrumbService,
  EnumValues,
  Note,
  UserLite,
  ModalService,
  NotesSidebarComponent,
  NotesModalData,
  EnumObjectExtended,
} from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import {
  ProductRecipe,
  ProductRecipeInsert,
  ProductRecipeIngredient,
  IngredientSubstitute,
  ProductRecipeIngredientFormGroup,
  IngredientSubstituteFormGroup,
} from '../../../models/product-recipe/product-recipe';
import { ProductDevelopmentAdminGroup, ProductDevelopmentEditorGroup } from '../../../models/authorization/enum/user-group.enum';
import { ItemMaster } from '../../../models/item-master';
import { EnumService } from '../../../services/enum.service';
import { LocaleHelperService } from '../../../services/localehelperservice.service';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { ProductRecipeProxyService } from '../../../proxies/item/product-recipe.proxy.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { ProductDevelopmentStatus, getValidStatusOptions } from '../../../models/enums/product-development-status';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';

@Component({
  selector: 'app-product-recipe-details',
  templateUrl: './product-recipe-details.component.html',
  styleUrl: './product-recipe-details.component.scss',
})
export class ProductRecipeDetailsComponent implements OnInit {
  private readonly productRecipeProxyService = inject(ProductRecipeProxyService);
  private readonly itemMasterProxyService = inject(ItemMasterProxyService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly userAuthService = inject(UserAuthenticationService);
  private readonly modalService = inject(ModalService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly enumService = inject(EnumService);
  private readonly localeHelper = inject(LocaleHelperService);
  private readonly translateService = inject(TranslateService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  readonly isLoading = signal(0);
  readonly recipeId = signal<string | null>(null);
  readonly isEditMode = signal(false);
  readonly notes = signal<Note[]>([]);
  readonly userLite = signal<UserLite | null>(null);
  readonly statuses = signal<EnumValues>({});
  readonly statusesEnum = signal<EnumObjectExtended | null>(null);
  readonly productRecipe = signal<ProductRecipe | null>(null);
  readonly chemicalRoleOptions = signal<{ label: string; value: number }[]>([]);

  // Recursive recipe and item master sub-rows caching and expansion states
  readonly subRecipesMap = signal<Map<string, ProductRecipe>>(new Map());
  readonly itemNamesMap = signal<Map<string, string>>(new Map());
  readonly expandedIngredients = signal<Set<AbstractControl>>(new Set());
  readonly expandedSubRecipes = signal<Set<string>>(new Set());

  // Autocomplete suggestions

  // Toggle: false = search by Item Master, true = search by Recipe
  readonly searchByRecipe = signal(false);

  // Item Master uses ItemStatus (Active=0), while recipes use ProductDevelopmentStatus.
  readonly allowedItemStatuses = [0, ProductDevelopmentStatus.Approved, ProductDevelopmentStatus.ReadyForProduction];
  readonly allowedIngredientStatuses = [ProductDevelopmentStatus.Approved, ProductDevelopmentStatus.ReadyForProduction];

  // Hardcoded UOM: kg = 0
  readonly HARDCODED_UOM = 0;

  saveMenuItems: MenuItem[] = [];

  canEdit = computed(() => {
    return (
      AuthService.hasRole(ProductDevelopmentAdminGroup.ProductRecipeAdmin) ||
      AuthService.hasRole(ProductDevelopmentEditorGroup.ProductRecipeEditor)
    );
  });

  isLockedStatus = computed(() => {
    const s = this.productRecipe()?.status;
    return s === ProductDevelopmentStatus.ReadyForProduction || s === ProductDevelopmentStatus.Inactive;
  });

  readonly productRecipeStatusOptions = computed(() => {
    const statuses = this.statuses();
    if (!statuses || typeof statuses !== 'object') return {};
    if (!this.canEdit()) return {};

    const savedStatus = this.productRecipe()?.status ?? ProductDevelopmentStatus.Draft;
    return getValidStatusOptions(savedStatus, statuses);
  });

  // Form controls
  readonly recipeNumber = new FormControl<string>('', { nonNullable: true });
  readonly chemicalRole = new FormControl<number | null>(null, [Validators.required]);
  readonly name = new FormControl<string | null>(null, [Validators.required]);
  // Set server-side when an ItemMaster is created from this recipe. UI displays read-only.
  readonly outputItemCode = new FormControl<string | null>(null);
  readonly waterConsumptionInLiters = new FormControl<number | null>(null, [Validators.min(0)]);
  readonly status = new FormControl<number>(ProductDevelopmentStatus.Draft, { nonNullable: true });

  readonly formGroup = new FormGroup({
    recipeNumber: this.recipeNumber,
    chemicalRole: this.chemicalRole,
    name: this.name,
    outputItemCode: this.outputItemCode,
    waterConsumptionInLiters: this.waterConsumptionInLiters,
    status: this.status,
  });

  getTotalQuantity(): number {
    const ingredientsTotal = this.ingredients.controls.reduce((sum, group) => {
      return sum + (group.controls.quantity.value || 0);
    }, 0);
    return ingredientsTotal + (this.waterConsumptionInLiters.value || 0);
  }

  readonly ingredients = new FormArray<FormGroup<ProductRecipeIngredientFormGroup>>([]);

  constructor() {
    effect(
      () => {
        const enumObj = this.enumService.getEnum('ProductDevelopmentStatus', 'itemEnums');
        if (enumObj && Object.keys(enumObj.values).length > 0) {
          this.statusesEnum.set(enumObj);
          this.statuses.set(enumObj.values);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const chemicalRoleValues = this.enumService.getEnum('ChemicalRole', 'itemEnums').values;
        if (Object.keys(chemicalRoleValues).length > 0) {
          this.chemicalRoleOptions.set(
            Object.entries(chemicalRoleValues).map(([key, value]) => ({
              label: value,
              value: Number(key),
            })),
          );
        }
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      const locked = this.isLockedStatus();
      if (locked) {
        this.name.disable();
        this.waterConsumptionInLiters.disable();
        this.ingredients.disable();
      } else if (this.canEdit()) {
        this.name.enable();
        this.waterConsumptionInLiters.enable();
        this.ingredients.enable();
      }
    });
  }

  ngOnInit(): void {
    this.userLite.set(this.userAuthService.userLite);

    if (!this.canEdit()) {
      this.formGroup.disable();
      this.ingredients.disable();
    }

    this.translateService.stream('Common_SaveAndClose').subscribe((label: string) => {
      this.saveMenuItems = [
        {
          label: label || 'Save and Close',
          icon: 'pi pi-save',
          command: () => this.onSave(true),
        },
      ];
    });

    this.route.queryParamMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.recipeId.set(id);
        this.isEditMode.set(true);
        this.loadRecipe(id);
      } else {
        this.breadcrumbService.setCurrent('Common_New');
      }
    });
  }

  private incrementLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private loadRecipe(id: string): void {
    this.incrementLoading();
    this.productRecipeProxyService
      .getById(id)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.productRecipe.set(response.data);
            this.breadcrumbService.setCurrent(response.data.number);
            this.populateForm(response.data);
          }
        },
      });
  }

  private populateForm(recipe: ProductRecipe): void {
    this.recipeNumber.setValue(recipe.number || '');
    this.chemicalRole.setValue(recipe.chemicalRole);
    // ChemicalRole is immutable once a recipe exists — to change it the user must create a new recipe.
    this.chemicalRole.disable();
    this.name.setValue(recipe.name);
    this.outputItemCode.setValue(recipe.outputItemCode);
    this.waterConsumptionInLiters.setValue(recipe.waterConsumptionInLiters ?? null);
    this.status.setValue(recipe.status);

    if (recipe.notes && recipe.notes.length > 0) {
      this.notes.set(recipe.notes);
    }

    // Populate ingredients
    this.ingredients.clear();
    if (recipe.ingredients && recipe.ingredients.length > 0) {
      recipe.ingredients.forEach((ingredient) => {
        this.ingredients.push(this.createIngredientGroup(ingredient));
      });
      this.resolveIngredientDisplayNames(recipe.ingredients);

      // Load sub-recipes recursively
      const recipeNumbers = recipe.ingredients.map((ing) => ing.productRecipeNumber).filter((num): num is string => !!num);
      this.loadSubRecipes(recipeNumbers);
    }
  }

  private resolveIngredientDisplayNames(ingredients: ProductRecipeIngredient[]): void {
    // Collect all item codes and recipe numbers (from ingredients + substitutes)
    const itemCodes = new Set<string>();
    const recipeNumbers = new Set<string>();

    ingredients.forEach((ing) => {
      if (ing.itemCode) itemCodes.add(ing.itemCode);
      if (ing.productRecipeNumber) recipeNumbers.add(ing.productRecipeNumber);
      ing.substitutes?.forEach((sub) => {
        if (sub.itemCode) itemCodes.add(sub.itemCode);
        if (sub.productRecipeNumber) recipeNumbers.add(sub.productRecipeNumber);
      });
    });

    const requests: Record<string, Observable<unknown>> = {};

    if (itemCodes.size > 0) {
      requests['items'] = this.itemMasterProxyService.getItemCodesBatch({ itemCodes: [...itemCodes] });
    }
    if (recipeNumbers.size > 0) {
      const recipeRequests = [...recipeNumbers].map((num) => this.productRecipeProxyService.getByNumber(num));
      requests['recipes'] = forkJoin(recipeRequests);
    }

    if (Object.keys(requests).length === 0) return;

    forkJoin(requests).subscribe((results) => {
      // Build lookup maps
      const itemNameMap = new Map<string, string>();
      const recipeNameMap = new Map<string, string>();

      if (results['items']) {
        const itemResponse = results['items'] as { data?: ItemMaster[] };
        itemResponse.data?.forEach((item) => {
          itemNameMap.set(item.itemCode, this.localeHelper.getLocalizedValue(item.itemLongDisplayName));
        });
      }

      if (results['recipes']) {
        const recipeResponses = results['recipes'] as { data?: ProductRecipe }[];
        recipeResponses.forEach((res) => {
          if (res.data) {
            recipeNameMap.set(res.data.number, res.data.name);
          }
        });
      }

      // Apply display names to ingredient and substitute form groups
      this.ingredients.controls.forEach((group) => {
        const code = group.controls.itemCode.value;
        const recNum = group.controls.productRecipeNumber.value;
        if (code && itemNameMap.has(code)) {
          group.controls.displayName.setValue(itemNameMap.get(code)!);
        } else if (recNum && recipeNameMap.has(recNum)) {
          group.controls.displayName.setValue(recipeNameMap.get(recNum)!);
        }

        group.controls.substitutes.controls.forEach((sub) => {
          const subCode = sub.controls.itemCode.value;
          const subRecNum = sub.controls.productRecipeNumber.value;
          if (subCode && itemNameMap.has(subCode)) {
            sub.controls.displayName.setValue(itemNameMap.get(subCode)!);
          } else if (subRecNum && recipeNameMap.has(subRecNum)) {
            sub.controls.displayName.setValue(recipeNameMap.get(subRecNum)!);
          }
        });
      });
    });
  }

  // Ingredient management
  createIngredientGroup(data?: ProductRecipeIngredient, displayName?: string): FormGroup<ProductRecipeIngredientFormGroup> {
    const group = new FormGroup<ProductRecipeIngredientFormGroup>(
      {
        itemCode: new FormControl<string | null>(data?.itemCode || null),
        productRecipeNumber: new FormControl<string | null>(data?.productRecipeNumber || null),
        quantity: new FormControl<number | null>(data?.quantity ?? null, [Validators.required, Validators.min(0.01)]),
        uom: new FormControl<number>(this.HARDCODED_UOM, { nonNullable: true }),
        displayName: new FormControl<string | null>(displayName || null),
        substitutes: new FormArray<FormGroup<IngredientSubstituteFormGroup>>(
          (data?.substitutes || []).map((sub) => this.createSubstituteGroup(sub)),
        ),
      },
      { validators: [this.itemCodeOrFormulaReferenceValidator] },
    );
    return group;
  }

  addIngredientByItem(item: ItemMaster): void {
    const localizedName = this.localeHelper.getLocalizedValue(item.itemLongDisplayName);
    const group = this.createIngredientGroup(
      { itemCode: item.itemCode, quantity: 0, uom: this.HARDCODED_UOM, substitutes: [] },
      localizedName,
    );
    this.ingredients.push(group);
  }

  addIngredientByRecipe(recipe: ProductRecipe): void {
    const group = this.createIngredientGroup(
      { productRecipeNumber: recipe.number, quantity: 0, uom: this.HARDCODED_UOM, substitutes: [] },
      recipe.name,
    );
    this.ingredients.push(group);

    if (recipe.number) {
      this.loadSubRecipes([recipe.number]);
    }
  }

  addIngredient(): void {
    this.ingredients.push(this.createIngredientGroup());
  }

  // Autocomplete search methods
  public fetchItems = (query: string): Observable<ItemMaster[]> =>
    this.itemMasterProxyService
      .postItemSearch({ itemCode: query, description: query }, false, true, true)
      .pipe(map((response) => (response.data?.items || []).filter((item) => this.allowedItemStatuses.includes(item.status))));

  public fetchRecipes = (query: string): Observable<ProductRecipe[]> => {
    const currentNumber = this.recipeNumber.value;

    return this.productRecipeProxyService
      .search({ number: query, name: query, status: this.allowedIngredientStatuses }, 1, 25, undefined, undefined, true)
      .pipe(
        map((response) => {
          const results = response.data?.items || [];
          return currentNumber ? results.filter((r) => r.number !== currentNumber) : results;
        }),
      );
  };

  onSubstituteItemSelected(event: AutoCompleteSelectEvent, ingredientIndex: number, substituteIndex: number): void {
    const item = event.value as ItemMaster;
    const sub = this.getSubstitutes(ingredientIndex).at(substituteIndex);
    if (sub) {
      sub.controls.itemCode.setValue(item.itemCode);
      sub.controls.productRecipeNumber.setValue(null);
      sub.controls.displayName.setValue(this.localeHelper.getLocalizedValue(item.itemLongDisplayName));
    }
  }

  onSubstituteRecipeSelected(event: AutoCompleteSelectEvent, ingredientIndex: number, substituteIndex: number): void {
    const recipe = event.value as ProductRecipe;
    const sub = this.getSubstitutes(ingredientIndex).at(substituteIndex);
    if (sub) {
      sub.controls.productRecipeNumber.setValue(recipe.number || null);
      sub.controls.itemCode.setValue(null);
      sub.controls.displayName.setValue(recipe.name);

      if (recipe.number) {
        this.loadSubRecipes([recipe.number]);
      }
    }
  }

  isRecipeIngredient(ingredientIndex: number): boolean {
    const group = this.ingredients.at(ingredientIndex);
    return !!group?.controls.productRecipeNumber.value;
  }

  removeIngredient(index: number): void {
    if (index >= 0 && index < this.ingredients.length) {
      this.ingredients.removeAt(index);
    }
  }

  // Substitute management
  createSubstituteGroup(data?: IngredientSubstitute, displayName?: string): FormGroup<IngredientSubstituteFormGroup> {
    return new FormGroup<IngredientSubstituteFormGroup>(
      {
        itemCode: new FormControl<string | null>(data?.itemCode || null),
        productRecipeNumber: new FormControl<string | null>(data?.productRecipeNumber || null),
        displayName: new FormControl<string | null>(displayName || null),
        priority: new FormControl<number | null>(data?.priority ?? 0),
      },
      { validators: [this.itemCodeOrFormulaReferenceValidator] },
    );
  }

  addSubstitute(ingredientIndex: number): void {
    const ingredientGroup = this.ingredients.at(ingredientIndex);
    if (ingredientGroup) {
      ingredientGroup.controls.substitutes.push(this.createSubstituteGroup());
    }
  }

  removeSubstitute(ingredientIndex: number, substituteIndex: number): void {
    const ingredientGroup = this.ingredients.at(ingredientIndex);
    if (ingredientGroup) {
      ingredientGroup.controls.substitutes.removeAt(substituteIndex);
    }
  }

  getSubstitutes(ingredientIndex: number): FormArray<FormGroup<IngredientSubstituteFormGroup>> {
    return this.ingredients.at(ingredientIndex).controls.substitutes;
  }

  // Custom validator: must have itemCode OR productRecipeNumber, not both, not neither
  private itemCodeOrFormulaReferenceValidator(control: AbstractControl): ValidationErrors | null {
    const group = control as FormGroup;
    const itemCode = group.get('itemCode')?.value;
    const productRecipeNumber = group.get('productRecipeNumber')?.value;
    const hasItemCode = !!itemCode && itemCode.trim().length > 0;
    const hasFormulaRef = !!productRecipeNumber && productRecipeNumber.trim().length > 0;

    if (!hasItemCode && !hasFormulaRef) {
      return { itemCodeOrFormulaRequired: true };
    }
    if (hasItemCode && hasFormulaRef) {
      return { itemCodeAndFormulaMutuallyExclusive: true };
    }
    return null;
  }

  onSave(navigateAway = false): void {
    if (this.formGroup.invalid || this.ingredients.invalid) {
      this.formGroup.markAllAsTouched();
      this.ingredients.markAllAsTouched();
      return;
    }

    this.confirmationService.confirm({
      key: 'main',
      header: this.translateService.instant('Common_Confirmation'),
      icon: 'pi pi-question-circle',
      message: this.translateService.instant('Common_Save_Confirm_Message'),
      rejectButtonProps: {
        label: this.translateService.instant('Common_Cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.translateService.instant('Common_Accept'),
      },
      accept: () => this.executeSave(navigateAway),
    });
  }

  private executeSave(navigateAway: boolean): void {
    const ingredientData: ProductRecipeIngredient[] = this.ingredients.controls.map((group) => {
      const substitutesData: IngredientSubstitute[] = group.controls.substitutes.controls.map((subGroup) => ({
        itemCode: subGroup.controls.itemCode.value || undefined,
        productRecipeNumber: subGroup.controls.productRecipeNumber.value || undefined,
        priority: subGroup.controls.priority.value || 0,
      }));

      return {
        itemCode: group.controls.itemCode.value || undefined,
        productRecipeNumber: group.controls.productRecipeNumber.value || undefined,
        quantity: group.controls.quantity.value!,
        uom: group.controls.uom.value!,
        substitutes: substitutesData,
      };
    });

    const isEdit = this.isEditMode();
    const currentId = this.recipeId();

    const baseData = {
      chemicalRole: this.chemicalRole.value!,
      name: this.name.value!,
      status: this.status.value,
      outputItemCode: this.outputItemCode.value,
      outputUOM: this.HARDCODED_UOM,
      baseQuantity: this.getTotalQuantity(),
      waterConsumptionInLiters: this.waterConsumptionInLiters.value ?? 0,
      ingredients: ingredientData,
      notes: this.notes(),
    };

    this.incrementLoading();
    if (isEdit && currentId) {
      const recipeData: ProductRecipe = {
        ...baseData,
        id: currentId,
        number: this.recipeNumber.value,
      };
      this.productRecipeProxyService
        .update(currentId, recipeData)
        .pipe(finalize(() => this.decrementLoading()))
        .subscribe({
          next: () => {
            GlobalMessageUtil.success(
              this.translateService.instant('Common_Success') || 'Success',
              this.translateService.instant('ProductRecipe_Updated') || 'Product Recipe updated successfully',
            );
            if (navigateAway) {
              this.navigateBack();
            } else {
              this.loadRecipe(currentId!);
            }
          },
        });
    } else {
      const insertData: ProductRecipeInsert = { ...baseData };
      this.productRecipeProxyService
        .create(insertData)
        .pipe(finalize(() => this.decrementLoading()))
        .subscribe({
          next: (response) => {
            GlobalMessageUtil.success(
              this.translateService.instant('Common_Success') || 'Success',
              this.translateService.instant('ProductRecipe_Created') || 'Product Recipe created successfully',
            );
            if (navigateAway) {
              this.navigateBack();
            } else if (response.data?.id) {
              this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { id: response.data.id },
                queryParamsHandling: 'merge',
              });
            }
          },
        });
    }
  }

  loadSubRecipes(recipeNumbers: string[]): void {
    if (recipeNumbers.length === 0) return;

    // Filter out already loaded recipes to avoid duplicate/infinite calls
    const toFetch = recipeNumbers.filter((num) => !this.subRecipesMap().has(num));
    if (toFetch.length === 0) return;

    this.incrementLoading();
    const requests = toFetch.map((num) => this.productRecipeProxyService.getByNumber(num));
    forkJoin(requests)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (responses) => {
          const newMap = new Map(this.subRecipesMap());
          const nextRecipeNumbers: string[] = [];
          const newItemCodes: string[] = [];

          responses.forEach((res) => {
            if (res.data) {
              const recipe = res.data;
              newMap.set(recipe.number, recipe);

              // Gather ingredients of this recipe
              recipe.ingredients?.forEach((ing) => {
                if (ing.productRecipeNumber) {
                  nextRecipeNumbers.push(ing.productRecipeNumber);
                }
                if (ing.itemCode) {
                  newItemCodes.push(ing.itemCode);
                }
                ing.substitutes?.forEach((sub) => {
                  if (sub.productRecipeNumber) {
                    nextRecipeNumbers.push(sub.productRecipeNumber);
                  }
                  if (sub.itemCode) {
                    newItemCodes.push(sub.itemCode);
                  }
                });
              });
            }
          });

          this.subRecipesMap.set(newMap);

          // Fetch item names for these nested items if we don't have them cached
          if (newItemCodes.length > 0) {
            const uniqueItemCodes = [...new Set(newItemCodes)].filter((code) => !this.itemNamesMap().has(code));
            if (uniqueItemCodes.length > 0) {
              this.itemMasterProxyService.getItemCodesBatch({ itemCodes: uniqueItemCodes }).subscribe({
                next: (itemRes) => {
                  const newItemNamesMap = new Map(this.itemNamesMap());
                  itemRes.data?.forEach((item) => {
                    newItemNamesMap.set(item.itemCode, this.localeHelper.getLocalizedValue(item.itemLongDisplayName));
                  });
                  this.itemNamesMap.set(newItemNamesMap);
                },
              });
            }
          }

          // Recursively load next level of sub-recipes
          if (nextRecipeNumbers.length > 0) {
            this.loadSubRecipes(nextRecipeNumbers);
          }
        },
      });
  }

  isIngredientExpanded(control: AbstractControl): boolean {
    return this.expandedIngredients().has(control);
  }

  toggleIngredientExpansion(control: AbstractControl): void {
    const next = new Set(this.expandedIngredients());
    if (next.has(control)) {
      next.delete(control);
    } else {
      next.add(control);
    }
    this.expandedIngredients.set(next);
  }

  isSubRecipeExpanded(recipeNumber: string): boolean {
    return this.expandedSubRecipes().has(recipeNumber);
  }

  toggleSubRecipeExpansion(recipeNumber: string): void {
    const next = new Set(this.expandedSubRecipes());
    if (next.has(recipeNumber)) {
      next.delete(recipeNumber);
    } else {
      next.add(recipeNumber);
    }
    this.expandedSubRecipes.set(next);
  }

  hasSubIngredients(recipeNumber: string | undefined): boolean {
    if (!recipeNumber) return false;
    const recipe = this.subRecipesMap().get(recipeNumber);
    return !!recipe?.ingredients && recipe.ingredients.length > 0;
  }

  getSubIngredients(recipeNumber: string | undefined): ProductRecipeIngredient[] {
    if (!recipeNumber) return [];
    return this.subRecipesMap().get(recipeNumber)?.ingredients || [];
  }

  onCancel(): void {
    this.navigateBack();
  }

  private navigateBack(): void {
    this.router.navigate(['/product-recipes']);
  }

  onViewMore(): void {
    this.modalService
      .open<NotesModalData>(
        NotesSidebarComponent,
        {
          modalClass: 'notes-sidebar-modal',
          position: 'right',
          closeOnVeilClick: true,
        },
        {
          notes: this.notes(),
          userLite: this.userLite()!,
          viewOnly: !this.canEdit(),
        },
      )
      .subscribe((result: unknown) => {
        const r = result as { data?: { notes: Note[] } };
        if (r?.data?.notes) {
          this.notes.set(r.data.notes);
        }
      });
  }
}
